import { useId, useState } from 'react';

import { Button, Dialog, Field, Input } from '../../../components/ui';
import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import type { ApiError } from '../../../lib/errors';
import { sinhMa } from '../../../lib/ma-tu-sinh';
import { LoiTrongHop } from '../../tenant/components/phan-form';
import { KHOA_LOI_TEN } from '../constants';
import { useDanhMucQuyen } from '../hooks/useVaiTro';
import {
  DAI_TOI_DA,
  kiemMa,
  kiemTenVaiTro,
  loiTheoConTro,
  loiTheoQuyen,
  sapTheoDanhMuc,
} from '../nghiep-vu/vai-tro';
import type { RoleCreate } from '../types';
import { ChonQuyen } from './ChonQuyen';

/** Tiền tố khi tên mở đầu bằng số hoặc chỉ một ký tự — mã phải mở đầu bằng chữ. */
const TIEN_TO_MA = 'VT';

/** Đường dẫn lỗi có ô trên form — lỗi khác gom vào dải chung. */
const O_CO_TREN_FORM = /^\/(code|name|permissions\/\d+)$/;

/**
 * Hộp thoại tạo vai trò (`POST /api/v1/roles`): tên, và quyền tuỳ chọn — để
 * trống thì phân quyền sau ở menu của dòng.
 *
 * Mã **tự sinh từ tên** (ADR-UI-0014), hiện cạnh ô tên để người dùng biết,
 * không cho gõ. Vì người dùng không thấy ô mã, lỗi của server về mã (`409`,
 * `/code` — trùng vai trò có sẵn) đặt dưới ô tên kèm cách sửa: đổi tên.
 * Quyền không gán được (`422`, `/permissions/{i}`) dưới đúng ô quyền. Hộp
 * không đóng khi lỗi.
 *
 * Hộp rộng vì lưới quyền xếp nhiều cột: tổ chức cài càng nhiều service SDK thì
 * danh mục càng dài, một cột hẹp buộc cuộn rất xa.
 */
export function FormTaoVaiTro({
  open,
  dangGui,
  onClose,
  onTao,
}: {
  open: boolean;
  dangGui: boolean;
  onClose: () => void;
  onTao: (du: RoleCreate) => Promise<unknown>;
}) {
  const { t } = useT();
  const dichLoi = useLoi();
  const formId = useId();
  const danhMuc = useDanhMucQuyen(open);

  const [ten, setTen] = useState('');
  const [daChon, setDaChon] = useState<Set<string>>(() => new Set());
  const [daGui, setDaGui] = useState(false);
  const [loiServer, setLoiServer] = useState<{
    loi: ApiError;
    truong: Record<string, string>;
    quyen: Record<string, string>;
  } | null>(null);

  const ma = sinhMa(ten, { tienTo: TIEN_TO_MA, toiDa: DAI_TOI_DA.code });
  const maTen = kiemTenVaiTro(ten);
  // Tên có chữ nhưng toàn ký hiệu thì không sinh được mã hợp lệ.
  const khongSinhDuocMa = !maTen && kiemMa(ma) !== null;
  const loiTen =
    (maTen && (daGui || maTen === 'dai') ? t(KHOA_LOI_TEN[maTen], { so: DAI_TOI_DA.name }) : undefined) ??
    (khongSinhDuocMa ? t('vaiTroLop2.fTenKhongSinhMa') : undefined) ??
    (loiServer?.truong['/code'] ? t('vaiTroLop2.fTrungMa', { ma }) : undefined) ??
    loiServer?.truong['/name'];

  // Lỗi đã nằm cạnh ô tên (mã trùng) thì không lặp lại ở dải chung.
  const loiChiOMa =
    loiServer &&
    Object.keys(loiServer.truong).length > 0 &&
    Object.keys(loiServer.truong).every((d) => d === '/code' || d === '/name');
  const loiKhongCoO = Object.entries(loiServer?.truong ?? {}).filter(([d]) => !O_CO_TREN_FORM.test(d));

  const bo = (duongDan: string) =>
    setLoiServer((cu) => {
      if (!cu || !(duongDan in cu.truong)) return cu;
      const { [duongDan]: _boQua, ...conLai } = cu.truong;
      return Object.keys(conLai).length === 0 ? null : { ...cu, truong: conLai };
    });

  const gui = async () => {
    setDaGui(true);
    if (maTen || khongSinhDuocMa || dangGui) return;
    const permissions = sapTheoDanhMuc(daChon, danhMuc.data ?? []);
    const du: RoleCreate = { code: ma, name: ten.trim(), ...(permissions.length ? { permissions } : {}) };
    setLoiServer(null);
    try {
      await onTao(du);
    } catch (err) {
      const truong = loiTheoConTro((err as ApiError).details);
      setLoiServer({ loi: err as ApiError, truong, quyen: loiTheoQuyen(truong, permissions) });
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t('vaiTroLop2.taoTieuDe')}
      width={1120}
      // Hộp rộng thì cho cao hơn mặc định 60vh của `Dialog`: lưới quyền là phần chính.
      styles={{ body: { maxHeight: '70vh', overflowY: 'auto' } }}
      destroyOnHidden
      footer={
        <div className="space-y-3">
          {loiServer && !loiChiOMa && (
            <LoiTrongHop>
              {dichLoi(loiServer.loi)}
              {loiKhongCoO.map(([duongDan, thongDiep]) => (
                <span key={duongDan} className="mt-1 block">
                  <span className="font-mono">{duongDan}</span>: {thongDiep}
                </span>
              ))}
            </LoiTrongHop>
          )}
          <div className="flex flex-wrap justify-end gap-3">
            <Button variant="outline" size="md" onClick={onClose}>
              {t('chung.huy')}
            </Button>
            <Button variant="primary" size="md" htmlType="submit" form={formId} loading={dangGui}>
              {dangGui ? t('vaiTroLop2.dangTao') : t('vaiTroLop2.taoVaiTro')}
            </Button>
          </div>
        </div>
      }
    >
      <form
        id={formId}
        noValidate
        className="space-y-4 pt-2"
        onSubmit={(e) => {
          e.preventDefault();
          void gui();
        }}
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t('vaiTroLop2.fTen')} required hint={t('vaiTroLop2.fTenGoiY')} error={loiTen}>
            {(p) => (
              <Input
                {...p}
                status={p['aria-invalid'] ? 'error' : undefined}
                autoFocus
                value={ten}
                onChange={(e) => {
                  setTen(e.target.value);
                  bo('/name');
                  // Mã đi theo tên: đổi tên là đã sửa lỗi mã trùng.
                  bo('/code');
                }}
              />
            )}
          </Field>
          <div className="space-y-1.5">
            <span className="block text-sm font-medium text-content-secondary">{t('vaiTroLop2.fMa')}</span>
            <span className="block min-h-6 break-all font-mono text-sm text-content-primary">
              {ma || <span className="font-sans text-content-muted">{t('vaiTroLop2.fMaChoTen')}</span>}
            </span>
            <p className="text-xs text-content-muted">{t('vaiTroLop2.fMaTuSinh')}</p>
          </div>
        </div>

        <section className="space-y-2" aria-labelledby={`${formId}-quyen`}>
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h3 id={`${formId}-quyen`} className="text-sm font-medium text-content-secondary">
              {t('vaiTroLop2.fQuyen')}
            </h3>
            <span className="text-xs text-content-muted">
              {t('vaiTroLop2.daChonSoQuyen', { so: daChon.size })}
            </span>
          </div>
          <p className="text-xs text-content-muted">{t('vaiTroLop2.fQuyenGoiY')}</p>
          {/* Không giới hạn chiều cao ở đây: thân Dialog đã tự cuộn (components/ui/Dialog).
              Cuộn hai tầng lồng nhau làm người dùng mất dấu vị trí trong lưới quyền. */}
          <div className="pr-1">
            <ChonQuyen
              danhMuc={danhMuc.data}
              dangTai={danhMuc.isLoading}
              loi={danhMuc.error}
              onThuLai={() => void danhMuc.refetch()}
              daChon={daChon}
              onDoi={(moi) => {
                setDaChon(moi);
                // Đã sửa lựa chọn thì lỗi theo quyền của lần gửi trước không còn đúng chỗ.
                if (loiServer && Object.keys(loiServer.quyen).length > 0) setLoiServer(null);
              }}
              loiTheoQuyen={loiServer?.quyen}
              luoi
            />
          </div>
        </section>
      </form>
    </Dialog>
  );
}
